/**
 * Spanish DNI/NIE Validation Utilities
 * 
 * Implements official Spanish ID validation with checksum verification
 * Supports both DNI (Spanish citizens) and NIE (foreign residents)
 * 
 * References:
 * - Real Decreto 1553/2005 (DNI format)
 * - Real Decreto 896/2003 (NIE format)
 */

// DNI checksum letters (official algorithm)
const DNI_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE'

// NIE prefix mapping for checksum calculation
const NIE_PREFIX_MAP = {
  'X': '0',
  'Y': '1', 
  'Z': '2'
}

/**
 * Validate DNI format and checksum
 * DNI format: 8 digits + 1 control letter
 * 
 * @param {string} dni - DNI to validate
 * @returns {Object} Validation result with isValid and error
 */
export const validateDNI = (dni) => {
  if (!dni || typeof dni !== 'string') {
    return {
      isValid: false,
      error: 'DNI es requerido'
    }
  }

  // Clean and normalize
  const cleanDNI = dni.toUpperCase().replace(/[-\s]/g, '')
  
  // Check format: exactly 9 characters
  if (cleanDNI.length !== 9) {
    return {
      isValid: false,
      error: 'DNI debe tener exactamente 9 caracteres'
    }
  }

  // Extract number and letter parts
  const numberPart = cleanDNI.slice(0, 8)
  const letterPart = cleanDNI.slice(8, 9)

  // Validate number part (8 digits)
  if (!/^\d{8}$/.test(numberPart)) {
    return {
      isValid: false,
      error: 'Los primeros 8 caracteres deben ser números'
    }
  }

  // Validate letter part
  if (!/^[A-Z]$/.test(letterPart)) {
    return {
      isValid: false,
      error: 'El último carácter debe ser una letra'
    }
  }

  // Calculate expected checksum letter
  const dniNumber = parseInt(numberPart, 10)
  const expectedLetter = DNI_LETTERS[dniNumber % 23]

  // Verify checksum
  if (letterPart !== expectedLetter) {
    return {
      isValid: false,
      error: `DNI inválido. La letra correcta sería ${expectedLetter}`
    }
  }

  return {
    isValid: true,
    error: null,
    formattedDNI: `${numberPart}-${letterPart}`
  }
}

/**
 * Validate NIE format and checksum
 * NIE format: 1 letter (X,Y,Z) + 7 digits + 1 control letter
 * 
 * @param {string} nie - NIE to validate
 * @returns {Object} Validation result with isValid and error
 */
export const validateNIE = (nie) => {
  if (!nie || typeof nie !== 'string') {
    return {
      isValid: false,
      error: 'NIE es requerido'
    }
  }

  // Clean and normalize
  const cleanNIE = nie.toUpperCase().replace(/[-\s]/g, '')
  
  // Check format: exactly 9 characters
  if (cleanNIE.length !== 9) {
    return {
      isValid: false,
      error: 'NIE debe tener exactamente 9 caracteres'
    }
  }

  // Extract parts
  const prefixLetter = cleanNIE.slice(0, 1)
  const numberPart = cleanNIE.slice(1, 8)
  const letterPart = cleanNIE.slice(8, 9)

  // Validate prefix letter (X, Y, or Z)
  if (!['X', 'Y', 'Z'].includes(prefixLetter)) {
    return {
      isValid: false,
      error: 'NIE debe empezar con X, Y o Z'
    }
  }

  // Validate number part (7 digits)
  if (!/^\d{7}$/.test(numberPart)) {
    return {
      isValid: false,
      error: 'Los caracteres 2-8 deben ser números'
    }
  }

  // Validate letter part
  if (!/^[A-Z]$/.test(letterPart)) {
    return {
      isValid: false,
      error: 'El último carácter debe ser una letra'
    }
  }

  // Calculate checksum using NIE algorithm
  const mappedPrefix = NIE_PREFIX_MAP[prefixLetter]
  const nieNumber = parseInt(mappedPrefix + numberPart, 10)
  const expectedLetter = DNI_LETTERS[nieNumber % 23]

  // Verify checksum
  if (letterPart !== expectedLetter) {
    return {
      isValid: false,
      error: `NIE inválido. La letra correcta sería ${expectedLetter}`
    }
  }

  return {
    isValid: true,
    error: null,
    formattedNIE: `${prefixLetter}-${numberPart}-${letterPart}`
  }
}

/**
 * Detect if input is DNI or NIE format
 * 
 * @param {string} input - ID string to analyze
 * @returns {string} Type: 'dni', 'nie', or 'unknown'
 */
export const detectIdType = (input) => {
  if (!input || typeof input !== 'string') return 'unknown'

  const clean = input.toUpperCase().replace(/[-\s]/g, '')
  
  if (clean.length !== 9) return 'unknown'
  
  // NIE starts with X, Y, or Z
  if (['X', 'Y', 'Z'].includes(clean[0])) {
    return 'nie'
  }
  
  // DNI starts with digit
  if (/^\d/.test(clean[0])) {
    return 'dni'
  }
  
  return 'unknown'
}

/**
 * Universal ID validation (DNI or NIE)
 * 
 * @param {string} id - Spanish ID to validate
 * @returns {Object} Validation result with type detection
 */
export const validateSpanishId = (id) => {
  const type = detectIdType(id)
  
  switch (type) {
    case 'dni':
      return {
        ...validateDNI(id),
        type: 'dni'
      }
    
    case 'nie':
      return {
        ...validateNIE(id),
        type: 'nie'
      }
    
    default:
      return {
        isValid: false,
        error: 'Formato de DNI/NIE no reconocido',
        type: 'unknown'
      }
  }
}

/**
 * Format Spanish ID for display
 * 
 * @param {string} id - Spanish ID to format
 * @returns {string} Formatted ID with hyphens
 */
export const formatSpanishId = (id) => {
  const validation = validateSpanishId(id)
  
  if (!validation.isValid) return id
  
  return validation.formattedDNI || validation.formattedNIE || id
}

/**
 * Mask Spanish ID for privacy (show first 2 and last 1 characters)
 * 
 * @param {string} id - Spanish ID to mask
 * @returns {string} Masked ID
 */
export const maskSpanishId = (id) => {
  if (!id || id.length < 4) return '***'
  
  const clean = id.replace(/[-\s]/g, '')
  const first2 = clean.slice(0, 2)
  const last1 = clean.slice(-1)
  const stars = '*'.repeat(Math.max(0, clean.length - 3))
  
  return `${first2}${stars}${last1}`
}

/**
 * Generate random valid DNI for testing
 * WARNING: Only for testing purposes!
 * 
 * @returns {string} Valid random DNI
 */
export const generateTestDNI = () => {
  // Generate random 8-digit number
  const randomNumber = Math.floor(Math.random() * 99999999)
  const paddedNumber = randomNumber.toString().padStart(8, '0')
  
  // Calculate checksum letter
  const checkLetter = DNI_LETTERS[parseInt(paddedNumber) % 23]
  
  return `${paddedNumber}${checkLetter}`
}

/**
 * Generate random valid NIE for testing
 * WARNING: Only for testing purposes!
 * 
 * @returns {string} Valid random NIE
 */
export const generateTestNIE = () => {
  // Random prefix
  const prefixes = ['X', 'Y', 'Z']
  const prefix = prefixes[Math.floor(Math.random() * prefixes.length)]
  
  // Generate random 7-digit number
  const randomNumber = Math.floor(Math.random() * 9999999)
  const paddedNumber = randomNumber.toString().padStart(7, '0')
  
  // Calculate checksum letter
  const mappedPrefix = NIE_PREFIX_MAP[prefix]
  const nieNumber = parseInt(mappedPrefix + paddedNumber)
  const checkLetter = DNI_LETTERS[nieNumber % 23]
  
  return `${prefix}${paddedNumber}${checkLetter}`
}